'use client';

import { ReactNode, useState, useEffect, useMemo } from 'react';

import { useRegionStore } from '@northladder/services';
import { DEFAULT_LOCALE, TLocale } from '@northladder/utilities';

import { LocaleSwitcher } from './components';
import { useI18n } from './hooks';
import { LOCAL_STORAGE_LOCALE_KEY, LocaleContext } from './types';

interface ILocaleProviderProps {
  /**
   * The children components to consume this context. Usually rest of the app.
   */
  children: ReactNode;
  /**
   * Classes to be passed to the underlying `LocaleSwitcher` component.
   */
  className?: string;
  /**
   * Determines whether the switcher should be displayed as a pill or floating banner.
   */
  isLocaleSwitcherInline?: boolean;
  /**
   * Determines whether to show the floating pill in the top of the header
   */
  isGlobalLocaleSwitcherVisible?: boolean;
}

/**
 * This determines the initial local
 * - Checks for the locale from `cache` to see the most recent selected locale.
 * - Checks for the locale from the `pathname` if none is set.
 */
const getInitialLocal = (): TLocale => {
  const cachedLocale = (localStorage.getItem(LOCAL_STORAGE_LOCALE_KEY) ||
    DEFAULT_LOCALE) as TLocale;
  return cachedLocale;
};

/**
 * -----------------------------------------------------------------------------
 * This wraps around the entire application to feed it with the active locale &
 * language context.
 */
export function LocaleProvider({
  children,
  className,
  isLocaleSwitcherInline,
  isGlobalLocaleSwitcherVisible = true,
}: ILocaleProviderProps) {
  const { tenantConfig } = useRegionStore();
  const availableLanguages = tenantConfig?.commonAppConfig?.locales;

  const { locale } = useI18n();
  const [activeLocale, setActiveLocale] = useState<TLocale>(locale);

  const handleUpdateLocale = (targetLocale: TLocale) => {
    setActiveLocale(() => targetLocale);
  };

  useEffect(() => {
    const initialLocale = getInitialLocal();

    handleUpdateLocale(initialLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memoizedValue = useMemo(
    () => ({
      activeLocale,
      availableLanguages: availableLanguages || [],
      onUpdateLocale: handleUpdateLocale,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLocale, availableLanguages]
  );

  return (
    <LocaleContext.Provider value={memoizedValue}>
      {isGlobalLocaleSwitcherVisible ? (
        <LocaleSwitcher
          className={className}
          isInline={isLocaleSwitcherInline}
        />
      ) : null}
      {children}
    </LocaleContext.Provider>
  );
}
